import React, { useState } from 'react';
import { Menu, Globe, Phone, Mail } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { languages, contactInfo } from '../data/mock';

const Header = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navItems = [
    { label: 'Home', id: 'home' },
    { label: 'Mission', id: 'mission' },
    { label: 'Services', id: 'services' },
    { label: 'Global Reach', id: 'global-reach' },
    { label: 'Contact', id: 'contact' }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
      <div className="container mx-auto px-4">
        {/* Top bar with contact info */}
        <div className="hidden md:flex items-center justify-end gap-6 py-2 text-sm text-gray-600 border-b border-gray-100">
          <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-2 hover:text-blue-600 transition-colors">
            <Phone className="w-4 h-4" />
            {contactInfo.phone}
          </a>
          <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-2 hover:text-blue-600 transition-colors">
            <Mail className="w-4 h-4" />
            {contactInfo.email}
          </a>
        </div>

        {/* Main navigation */}
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <div className="relative w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white shadow-lg">
              <Globe className="w-6 h-6 absolute opacity-30" />
              <span className="relative font-bold text-xs">CACS</span>
            </div>
            <div>
              <h1 className="font-bold text-lg text-gray-900">Child Advocacy Consulting Services</h1>
              <p className="text-xs text-gray-600">Serving {contactInfo.servingCountries} Countries Worldwide</p>
            </div>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Language selector and mobile menu */}
          <div className="flex items-center gap-4">
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger className="w-[140px] border-gray-300">
                <Globe className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Mobile menu */}
            <Sheet>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="outline" size="icon">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <nav className="flex flex-col gap-4 mt-8">
                  {navItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="text-left text-lg text-gray-700 hover:text-blue-600 font-medium transition-colors py-2"
                    >
                      {item.label}
                    </button>
                  ))}
                  <div className="pt-4 border-t border-gray-200 space-y-3">
                    <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-2 text-gray-600 hover:text-blue-600">
                      <Phone className="w-4 h-4" />
                      {contactInfo.phone}
                    </a>
                    <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-2 text-gray-600 hover:text-blue-600">
                      <Mail className="w-4 h-4" />
                      {contactInfo.email}
                    </a>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
